// @ts-nocheck
/**
 * TabStickies Component
 *
 * Container for tab-specific sticky notes with PostgreSQL persistence.
 * Renders UserStickyPanel components for each sticky note on the current tab.
 *
 * Features:
 * - Auto-load stickies for current tab on mount
 * - Draggable, collapsible sticky notes
 * - Create button in fixed position
 * - Delete confirmation modal
 * - Loading and error states
 * - Portal rendering to prevent "Node cannot be found" errors
 */

import React, { useCallback, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import { Portal } from '@shared/components/ui/Portal';
import { useTabStickies } from '@shared/hooks/useTabStickies';
import { UserStickyPanel } from '@shared/components/UserStickies/UserStickyPanel';
import { UserStickyPillDock } from '@shared/components/UserStickies/UserStickyPillDock';
import type { TabStickyNote } from '@shared/types/tabStickies';
import type { StickyPosition, UserStickyNote } from '@shared/types/userStickies';
import { stickyColorSchemes } from '@shared/types/userStickies';
import { logger } from '@shared/utils/logger';

/**
 * Confirmation Modal for delete
 */
interface DeleteModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteModalProps> = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-dark-surface rounded-xl shadow-2xl shadow-black/20 p-6 max-w-sm mx-4"
      >
        <h3 className="text-lg font-semibold text-white mb-2">
          Delete Sticky Note?
        </h3>
        <p className="text-dark-muted mb-4">
          This action cannot be undone. The sticky note will be permanently deleted.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-dark-text bg-dark-input rounded-lg hover:bg-dark-surface/50 transition-all duration-150"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-all duration-150"
          >
            Delete
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// CreateStickyButton removed - creation now happens via UserStickies menu

/**
 * Error display component
 */
interface ErrorDisplayProps {
  error: string;
  onRetry: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <div className="fixed bottom-6 left-6 z-40 bg-red-900/30 border border-red-800 rounded-xl p-4 max-w-sm shadow-lg">
    <div className="flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-red-800">
          Failed to load sticky notes
        </p>
        <p className="text-xs text-red-400 mt-1">{error}</p>
        <button
          onClick={onRetry}
          className="text-sm text-red-700 underline mt-2 hover:text-red-800"
        >
          Try again
        </button>
      </div>
    </div>
  </div>
);

/**
 * Convert TabStickyNote to UserStickyNote format for UserStickyPanel
 */
const toUserStickyFormat = (sticky: TabStickyNote): UserStickyNote => ({
  id: sticky.id,
  title: sticky.title,
  content: sticky.content,
  isCollapsed: sticky.is_collapsed,
  position: {
    x: sticky.position_x,
    y: sticky.position_y,
  },
  color: sticky.color,
  createdAt: sticky.created_at ? new Date(sticky.created_at).getTime() : Date.now(),
  updatedAt: sticky.updated_at ? new Date(sticky.updated_at).getTime() : Date.now(),
});

/**
 * Main TabStickies Component
 */
export const TabStickies: React.FC = () => {
  const {
    stickies,
    isLoading,
    error,
    deleteModalOpen,
    addSticky,
    setContent,
    setTitle,
    setPosition,
    toggleCollapsed,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
    bringToFront,
    loadStickies,
  } = useTabStickies();

  // Debug logging for stickies state changes
  useEffect(() => {
    logger.log(`[TabStickies] State update - ${stickies.length} stickies:`,
      stickies.map(s => ({ id: s.id.slice(0, 8), title: s.title, content: s.content?.slice(0, 20) }))
    );
  }, [stickies]);

  // Split stickies into expanded and collapsed
  const expandedStickies = stickies.filter((s) => !s.is_collapsed);
  const collapsedStickies = stickies.filter((s) => s.is_collapsed);

  // Handle create
  const handleCreate = useCallback(async () => {
    try {
      await addSticky();
    } catch (err) {
      console.error('[TabStickies] Create failed:', err);
    }
  }, [addSticky]);

  // Handle position change from UserStickyPanel
  const handlePositionChange = useCallback(
    (id: string) => (position: StickyPosition) => {
      logger.log(`[TabStickies] Position change for ${id.slice(0, 8)}: x=${position.x}, y=${position.y}`);
      setPosition(id, position.x, position.y);
      bringToFront(id);
    },
    [setPosition, bringToFront]
  );

  // Don't render anything if no stickies to show
  const hasContent = expandedStickies.length > 0 || collapsedStickies.length > 0 || error || deleteModalOpen;
  if (!hasContent) {
    return null;
  }

  return (
    <Portal>
      {/* Error display */}
      {error && <ErrorDisplay error={error} onRetry={loadStickies} />}

      {/* Expanded stickies */}
      <AnimatePresence mode="popLayout">
        {expandedStickies.map((sticky) => (
          <UserStickyPanel
            key={sticky.id}
            sticky={toUserStickyFormat(sticky)}
            onContentChange={(content) => setContent(sticky.id, content)}
            onTitleChange={(title) => setTitle(sticky.id, title)}
            onPositionChange={handlePositionChange(sticky.id)}
            onMinimize={() => toggleCollapsed(sticky.id)}
            onDelete={() => openDeleteModal(sticky.id)}
          />
        ))}
      </AnimatePresence>

      {/* Collapsed stickies pill dock - only show if there are collapsed stickies */}
      {collapsedStickies.length > 0 && (
        <UserStickyPillDock
          collapsedStickies={collapsedStickies.map(toUserStickyFormat)}
          onRestore={(id) => toggleCollapsed(id)}
          onCreateNew={handleCreate}
          dockDisplayMode="full"
          onCycleDockDisplayMode={() => {}}
          onSetDockDisplayMode={() => {}}
        />
      )}

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {deleteModalOpen && (
          <DeleteConfirmModal
            isOpen={deleteModalOpen}
            onConfirm={confirmDelete}
            onCancel={closeDeleteModal}
          />
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default TabStickies;

